
// Component: SmartOrderForm.jsx
// Purpose: Form to place manual SmartTrade orders.
import React, { useState, useEffect } from 'react';
import api, { services } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import ManualOrdersPanel from './ManualOrdersPanel';
import { Loader, Sheet } from '../ds';
import { useBreakpoint } from '../../hooks/useBreakpoint';
//* Shared reject-reason map — also used by ManualOrdersPanel.
import { rejectReasonLabel } from '../../utils/rejectReason';
//* Shared helpers still used for broker display-name resolution (NOT the gating decision below).
import { buildBrokerMaps, extractBrokerList } from '../../utils/brokerCapabilities';
//! D3 — last-line guarantee that an error message can never crash the order form (see the util).
import { toDisplayText } from '../../utils/toDisplayText';
//* order-instrument-normalization (R6.1.1/R6.1.2/R6.2/R6.3/R6.4) — class-keyed, FAIL-CLOSED
//  capability gate REPLACED brokerCapabilities.js's isProductAllowed/isOrderTypeAllowed for the
//  gating decision. Those dead functions were REMOVED in loop-back-4 (zero production callers);
//  brokerCapabilities.js now exports only buildBrokerMaps/extractBrokerList (still used here).
//* Defect B (#28) B5 — `resolveAccountGateState` extends the SAME shared gate module
//  TradeExecutionStep consumes (not a new/parallel gate); this component previously had NO
//  `manifestLoaded`/state-branching at all (SME reuse-map B5 — "strictly thinner/worse").
//  UNKNOWN_INSTRUMENT (the former bare `UNKNOWN_CLASS_KEY` comparison) is now resolved INSIDE
//  `resolveAccountGateState` itself (instrumentClassCapabilityGate.js) — this component only
//  needs `instrumentClass`'s raw value to pass through, not the sentinel constant directly.
import {
  resolveInstrumentClassKey,
  buildClassKeyedCapabilityGate,
  fetchCapabilitiesForAccounts,
  resolveAccountGateState,
} from '../../utils/instrumentClassCapabilityGate';

// Exported for the N1 round-trip contract test (order-instrument-normalization): the token this
// returns MUST canonicalize (backend canonicalize_product_type) to the SAME canonical Product the
// gate keys on (UI_PRODUCT_TO_CANONICAL below), else the placed product != the gated product.
export const mapProductType = (pt) => {
    if (pt === 'INTRADAY') return 'MIS';   // canonicalize_product_type('MIS')  -> Product.INTRADAY
    if (pt === 'DELIVERY') return 'CNC';   // canonicalize_product_type('CNC')  -> Product.DELIVERY
    if (pt === 'FUTURES' || pt === 'OPTIONS') return 'NRML'; // 'NRML' -> Product.CARRYFORWARD (F&O)
    //--> N1 (loop-back-2, order-instrument-normalization): MARGIN (MTF) MUST send a token that
    //    canonicalizes to Product.MARGIN. The prior 'NRML' was a CRITICAL live mis-placement:
    //    canonicalize_product_type('NRML') -> Product.CARRYFORWARD (wire "M"), NOT Product.MARGIN
    //    (wire "F"/ "mtf") — a user picking "MTF" was silently placed CARRYFORWARD (real live order
    //    7a78bb26 stored CARRYFORWARD). 'MARGIN' canonicalizes to Product.MARGIN (canonicalize.py:38),
    //    which the adapter manifests translate to FT wire "F" / ICICI wire "mtf" — the true MTF
    //    product. This matches the gating axis (UI_PRODUCT_TO_CANONICAL.MARGIN='MARGIN' below), so
    //    the placed product now equals the gated product.
    if (pt === 'MARGIN') return 'MARGIN';
    return 'MIS';
};

//--> order-instrument-normalization R6.1.1/R6.2 — best-effort mapping from this legacy 6-option
//    UI dropdown to the canonical `Product` enum (order-management, 02-design.md §3.1: INTRADAY /
//    DELIVERY / CARRYFORWARD / MARGIN — 4 members only). FUTURES/OPTIONS have historically shared
//    the NRML wire code with MARGIN (see mapProductType above); CARRYFORWARD is that concept's
//    new canonical name, so they map there for gating purposes.
//    F11 (Stage-4 loop-back, arch-critic 02-reviews.md): the legacy `MTF` option is RETIRED —
//    canonical `MARGIN` (relabelled "MTF (Margin Trading Facility)") is now the SOLE MTF
//    representation. The prior residual gap noted here (MTF always fails-closed under the new
//    gate) no longer applies — `productAllowed('MARGIN')` already reads TRUE wherever a manifest
//    declares Product.MARGIN (ICICI order_manifest.py:141-142, FlatTrade order_manifest.py:122).
//! #144-22 V1-D5 — refusal codes whose server `detail.message` is the human-readable reason
//! (the roll-eve cutoff time / why the roll date is unconfirmed). Shown verbatim, never as a code label.
const SERVER_MESSAGE_REJECT_CODES = new Set(['roll_eve_stand_down', 'roll_policy_unresolved']);

export const UI_PRODUCT_TO_CANONICAL = {
  INTRADAY: 'INTRADAY',
  DELIVERY: 'DELIVERY',
  MARGIN: 'MARGIN',
  FUTURES: 'CARRYFORWARD',
  OPTIONS: 'CARRYFORWARD',
};
//--> Order-type UI values ('MARKET'/'LIMIT'/'SL-M') already match the canonical `OrderType`
//    VALUES verbatim (order_taxonomy keeps the existing SL_M value "SL-M") — no mapping needed.
//    Validity UI values: DAY/IOC match `TimeInForce` (alias of OrderValidity) directly. F11:
//    'VTC' is formally OUT-OF-TAXONOMY (not a canonical TimeInForce member — not placeable on
//    either live adapter) and its dead FE affordance has been removed; the validity control now
//    offers only DAY/IOC.

const SmartOrderForm = ({ instrument }) => {
  const { user } = useAuth();
  //! Phone-only order confirmation: this form places REAL money and desktop
  //! submits on a single button press. On a phone that press is a fat-finger
  //! away from a live order, so the phone path reviews the order in a Sheet
  //! first (see the confirm Sheet at the foot of this component). Desktop
  //! behaviour is deliberately UNCHANGED.
  const { isPhone } = useBreakpoint();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [action, setAction] = useState('BUY'); // BUY | SELL
  const [orderType, setOrderType] = useState('LIMIT'); // LIMIT | MARKET | SL-M
  const [productType, setProductType] = useState('INTRADAY'); // INTRADAY | DELIVERY | ...
  const [quantity, setQuantity] = useState(1);
  const [instrumentMeta, setInstrumentMeta] = useState(null);
  const [price, setPrice] = useState(instrument?.ltp || 0);
  const [triggerPrice, setTriggerPrice] = useState(0); 
  const [validity, setValidity] = useState('DAY'); // DAY | IOC — VTC retired (F11, out-of-taxonomy)
  
  // Broker Accounts State
  const [accounts, setAccounts] = useState([]);
  const [brokersKeyMap, setBrokersKeyMap] = useState({});
  const [selectedAccounts, setSelectedAccounts] = useState([]); // Array of account_ids
  //--> order-instrument-normalization R6.1.1/R6.1.2: broker_id -> classKey -> ClassBlock, fetched
  //    from GET /brokers/{account}/capabilities (via gateway). {} until a successful fetch
  //    populates it — the fail-closed default (every axis helper reads "no block" until then).
  const [manifestByBrokerAndClass, setManifestByBrokerAndClass] = useState({});
  //--> Defect B (#28) B3/B5 — per-broker fetch-status sibling map, `{ [broker_id]: 'ok'|'error' }`,
  //    from `fetchCapabilitiesForAccounts`'s additive envelope. Mirrors TradeExecutionStep's
  //    `brokerLoadStatus`.
  const [brokerLoadStatus, setBrokerLoadStatus] = useState({});
  //--> Defect B (#28) B5 (row 7) — this component had NO manifest-settled flag at all before,
  //    so every dropdown transiently rendered "— not supported" for every account on every page
  //    load until the async fetch resolved. Mirrors TradeExecutionStep's `manifestLoaded`.
  const [manifestLoaded, setManifestLoaded] = useState(false);

  const [loading, setLoading] = useState(false);
  const [notification, setNotification] = useState(null);
  
  //! liveEnabled: false until backend MANUAL_LIVE_ENABLED flag confirms — fail-safe default
  const [liveEnabled, setLiveEnabled] = useState(false);
  //! credential-fix follow-up (2026-07-02, issue manual-live-icici-appkey-empty): the key
  //! MUST rotate per logical placement. Generated once at mount (below), then rotated after
  //! each RECEIVED terminal response (see handleSubmit). Previously it was mint-once-at-mount
  //! with no setter → every placement reused it → OMS deduped every order after the first.
  const [idempotencyKey, setIdempotencyKey] = useState(() => crypto.randomUUID());
  const [armedOrders, setArmedOrders] = useState([]);
  //! Bumped after EVERY placement response (submitted / filled / rejected / unknown) so
  //!  ManualOrdersPanel re-fetches and shows the just-placed order — INCLUDING rejected ones,
  //!  which are intentionally never armed and would otherwise never appear in the list (#1).
  const [ordersRefreshSignal, setOrdersRefreshSignal] = useState(0);

  // Fetch MANUAL_LIVE_ENABLED flag from backend config on mount
  // --> Fail-safe: default PAPER-locked on error; never default to LIVE
  useEffect(() => {
    const fetchManualModeConfig = async () => {
      try {
        const res = await api.get('/config/manual-mode');
        const flagEnabled = res?.data?.data?.live_enabled === true;
        console.log('[DIAG] manual-mode config fetch', { live_enabled: flagEnabled });
        setLiveEnabled(flagEnabled);
      } catch (err) {
        console.error('[DIAG] manual-mode config fetch failed', err);
        setLiveEnabled(false);
      }
    };
    fetchManualModeConfig();
  }, []);

  useEffect(() => {
    if (!instrument?.instrument_id) return;
    let cancelled = false;
    services.instrumentMaster.getInstrument(instrument.instrument_id)
      .then(res => {
        if (!cancelled && res?.data) {
          setInstrumentMeta({
            lot_size: res.data.lot_size,
            contract_multiplier: res.data.contract_multiplier
          });
        }
      })
      .catch(err => console.error('[DIAG] Instrument meta fetch failed:', err));
    return () => { cancelled = true; };
  }, [instrument?.instrument_id]);

  // Fetch Broker Accounts & Broker Data on Mount
  useEffect(() => {
    const fetchData = async () => {
        try {
            // Parallel fetch
            const [accountsRes, brokersRes] = await Promise.all([
                services.brokerAccounts.getAccounts(),
                services.brokerPlatforms.getAllBrokers()
            ]);

            // Process Brokers
            //! Keyed on broker_id via the shared helper — BIS items carry `broker_id`, NOT
            //! `id`; the old b.id keying mapped everything under `undefined`, so the display-name
            //! map missed and rendered raw broker IDs instead of names.
            //! GET /brokers/ returns the envelope {status, data:[...], total} — the old
            //! `res.data.results` fallback matched nothing, so the broker list was [].
            //! F11 (Stage-4 loop-back): only the display-name map (`maps.names`) is consumed here
            //! now — `maps.metas` (migration-008 `api_capabilities` blob) is no longer read by
            //! this component; its last consumer (the MTF-visibility option) was retired below.
            const maps = buildBrokerMaps(extractBrokerList(brokersRes.data));
            setBrokersKeyMap(maps.names);

            // Process Accounts
            if (accountsRes.data && accountsRes.data.length > 0) {
                setAccounts(accountsRes.data);
                // Default to single account selected
                setSelectedAccounts([accountsRes.data[0].account_id]);
            }
        } catch (err) {
            console.error("Failed to fetch data:", err);
            setNotification({ type: 'error', msg: 'Failed to load broker data' });
        }
    };
    fetchData();
  }, []);

  //--> order-instrument-normalization R5.2.1/R6.1.2 — fetch the class-keyed capability manifest
  //    for every currently-selected account's broker; re-fetches on selection/account changes.
  //    ANY fetch failure/timeout/non-200 leaves the affected broker's key absent from the map
  //    (see fetchCapabilitiesForAccounts) -> buildClassKeyedCapabilityGate fails that broker
  //    CLOSED — never the legacy permit.
  useEffect(() => {
    let cancelled = false;
    if (selectedAccounts.length === 0) {
        setManifestByBrokerAndClass({});
        setBrokerLoadStatus({});
        setManifestLoaded(true); // nothing to load — never show a stale "checking" state
        return undefined;
    }
    setManifestLoaded(false);
    //--> Defect B (#28) B3/B5 — `fetchCapabilitiesForAccounts` now returns the additive envelope
    //    `{ manifests, status }`; `manifests` feeds the SAME `manifestByBrokerAndClass` state every
    //    existing gate consumer already reads (unchanged shape), `status` is the per-broker
    //    fetch-status map `resolveAccountGateState` needs.
    fetchCapabilitiesForAccounts(accounts, selectedAccounts).then(({ manifests, status }) => {
        if (!cancelled) {
            setManifestByBrokerAndClass(manifests);
            setBrokerLoadStatus(status);
            setManifestLoaded(true);
        }
    });
    return () => { cancelled = true; };
  }, [accounts, selectedAccounts]);

  const toggleAccount = (accountId) => {
    setSelectedAccounts(prev => {
        if (prev.includes(accountId)) {
            return prev.filter(id => id !== accountId);
        } else {
            return [...prev, accountId];
        }
    });
  };

  const handleSubmit = async () => {
    setLoading(true);
    setNotification(null);

    if (selectedAccounts.length === 0) {
        setNotification({ type: 'error', msg: 'Please select a broker account' });
        setLoading(false);
        return;
    }

    if (selectedAccounts.length > 1) {
        setNotification({ type: 'error', msg: 'Manual trading supports one account at a time' });
        setLoading(false);
        return;
    }

    try {
        const accId = selectedAccounts[0];

        // --> F1(b): resolve broker mapping by broker_id for the selected account
        const acc = accounts.find(a => a.account_id === accId);
        const isPaperAccount = acc?.broker_is_paper === true;

        let resolvedSymbol, resolvedToken, resolvedExchange, resolvedBrokerId;
        if (acc) {
            try {
                const mappingRes = await services.instrumentMaster.getTradingSymbols([instrument.instrument_id]);
                // Envelope shape confirmed from WatchList.jsx L317: response.data.instruments (no extra .data wrapper)
                const mappings = mappingRes?.data?.instruments?.[0]?.broker_mappings || [];
                // Match by numeric broker_id — NEVER by index or broker_name
                const m = mappings.find(x => Number(x.broker_id) === Number(acc?.broker_id));
                console.log('[DIAG] SmartTrade UI-carry resolve', {
                    instrument_im_id: instrument.instrument_id,
                    account_broker_id: acc?.broker_id,
                    matched: !!m,
                    resolved_symbol: m?.broker_symbol,
                });
                if (m) {
                    resolvedSymbol = m.broker_symbol;
                    resolvedToken = m.broker_token;
                    resolvedExchange = m.exchange;
                    resolvedBrokerId = m.broker_id;
                }
            } catch (mappingErr) {
                // Log but do not abort — for PAPER mode a missing mapping is acceptable;
                // the OMS live-only F2 guard fail-closes if these fields are absent on LIVE.
                console.log('[DIAG] SmartTrade UI-carry mapping fetch failed (non-fatal)', { error: mappingErr?.message });
            }
        }

        const payload = {
            instrument_id: `${instrument.exchange}:${instrument.symbol}`,
            instrument_im_id: instrument.instrument_id,
            exchange: instrument.exchange,
            symbol: instrument.symbol,
            instrument_type: instrument.instrument_type || 'EQUITY',
            broker_account_ids: [accId],
            side: action,
            order_type: orderType,
            lots: Number(quantity),
            limit_price: orderType === 'MARKET' ? null : Number(price),
            trigger_price: orderType === 'SL-M' ? Number(triggerPrice) : null,
            product_type: mapProductType(productType),
            validity: validity,
            execution_mode: (isPaperAccount ? 'PAPER' : 'LIVE'),
            idempotency_key: idempotencyKey,
            // F1(b) UI-carry fields — additive; only present when mapping resolved
            ...(resolvedSymbol !== undefined && { resolved_symbol: resolvedSymbol }),
            ...(resolvedToken !== undefined && { broker_token: resolvedToken }),
            ...(resolvedExchange !== undefined && { resolved_exchange: resolvedExchange }),
            ...(resolvedBrokerId !== undefined && { broker_id: resolvedBrokerId }),
        };
        const response = await services.trading.placeOrder(payload);
        const orderResult = response.data?.data?.results?.[0];
        //--> #144-22 WP4 — an accepted last-session manual entry keeps the existing response and
        //*   notification paths; its additive rollover warning only replaces ordinary success copy.
        const rolloverWarningMessage = response.data?.data?.warning?.message;
        const acceptedRolloverNotification = (
            typeof rolloverWarningMessage === 'string' && rolloverWarningMessage.trim()
        ) ? { type: 'warning', msg: rolloverWarningMessage } : null;
        //--> Branch on orderResult.status — a REJECTED order returns HTTP 200 with status='REJECTED'.
        //    Never show success for a rejection; never arm a rejected order.
        if (orderResult) {
            const status = String(orderResult.status || '').toUpperCase();
            console.log('[DIAG] placeOrder result', { status, order_id: orderResult.order_id, reject_reason: orderResult.reject_reason });
            if (status === 'REJECTED') {
                //! Order was rejected — display human-readable reason; do NOT setArmedOrders.
                const reasonMsg = rejectReasonLabel(orderResult.reject_reason)
                    || 'Order Rejected — see Orders panel for details';
                setNotification({ type: 'error', msg: reasonMsg });
            } else if (status === 'FILLED') {
                setNotification(
                    acceptedRolloverNotification || { type: 'success', msg: 'Order Filled' }
                );
                setArmedOrders(prev => [...prev, {
                    order_id: orderResult.order_id,
                    sentinel_sii: orderResult.sentinel_sii
                }]);
            } else if (status === 'SUBMITTED' || status === 'OPEN' || status === 'PENDING') {
                setNotification(
                    acceptedRolloverNotification || { type: 'success', msg: 'Order Submitted' }
                );
                setArmedOrders(prev => [...prev, {
                    order_id: orderResult.order_id,
                    sentinel_sii: orderResult.sentinel_sii
                }]);
            } else {
                //--> Unknown/missing status — warn; still arm so the panel can reconcile via WS/REST.
                setNotification({ type: 'warning', msg: 'Order status unknown — check Orders panel' });
                setArmedOrders(prev => [...prev, {
                    order_id: orderResult.order_id,
                    sentinel_sii: orderResult.sentinel_sii
                }]);
            }
        } else {
            setNotification({ type: 'warning', msg: 'Order status unknown — check Orders panel' });
        }
        //--> #1 fix: a RESPONSE was received (any status) — refresh the orders panel so the
        //    just-placed order (INCLUDING rejected, which is never armed) appears immediately
        //    rather than only as a transient toast.
        setOrdersRefreshSignal(s => s + 1);
        //! credential-fix follow-up (2026-07-02, issue manual-live-icici-appkey-empty) — a
        //! RESPONSE was received (submitted / filled / rejected / idempotent-hit / unknown), so
        //! this key's logical placement is DONE. Rotate it so the NEXT placement is a distinct
        //! order (not deduped to this one). On a THROWN error we deliberately do NOT reach here,
        //! so a manual retry of an ambiguous send reuses the same key = double-send safe.
        setIdempotencyKey(crypto.randomUUID());
    } catch (err) {
        console.error("Order Failed:", err);
        // F5: extract a STRING from the error — never assign raw object to msg
        const detail = err.response?.data?.detail;
        let errMsg;
        if (detail && typeof detail === 'object') {
            //! #144-22 V1-D5 — the FUTURE roll refusals carry their OWN human message (the
            //! cutoff time, what to do); a static code label would hide it.
            const serverMessage = typeof detail.message === 'string' ? detail.message.trim() : '';
            if (SERVER_MESSAGE_REJECT_CODES.has(detail.code) && serverMessage) {
                errMsg = serverMessage;
            } else {
                // Structured detail object — prefer human label from rejectReasonLabel, then fields
                errMsg = rejectReasonLabel(detail.code) || detail.message || detail.code || JSON.stringify(detail);
            }
        } else if (detail && typeof detail === 'string') {
            errMsg = detail;
        } else {
            errMsg = err.response?.data?.error?.message || err.message || 'Order Failed';
        }
        setNotification({ type: 'error', msg: errMsg });
    } finally {
        setLoading(false);
    }
  };

  const themeColor = action === 'BUY' ? 'bg-profit' : 'bg-loss';
  const themeTextColor = action === 'BUY' ? 'text-profit' : 'text-loss';
  const borderColor = action === 'BUY' ? 'border-profit' : 'border-loss';

  // Derived state for execution mode logic
  const selectedAcc = accounts.find(a => a.account_id === selectedAccounts[0]);
  const isPaperAccount = selectedAcc?.broker_is_paper === true;
  const isSubmitDisabled = loading || (selectedAccounts.length > 0 && !isPaperAccount && !liveEnabled);

  //--> order-instrument-normalization (R6.1.1/R6.1.2/R6.2) — class-keyed, FAIL-CLOSED capability
  //    gate REPLACES the broker-only migration-008 gate above for the enable/disable decision.
  //    F11 (Stage-4 loop-back): this retires SmartOrderForm's LAST migration-008 gating read
  //    (brokersMetaMap/selectedBrokerMeta/brokerCaps/api_capabilities, the former MTF-visibility
  //    read below — now deleted). The only remaining consumer of that blob platform-wide is the
  //    admin-only BrokerPlatformDetails.jsx informational panel (read-only, never gates an order
  //    — R5.2.4's documented split), unaffected by this change.

  //! DEFECT-2 fix — resolves to the UNKNOWN sentinel (never 'EQUITY') on a missing/blank/
  //! unrecognized instrument_type or exchange; see instrumentClassCapabilityGate.js.
  const instrumentClass = resolveInstrumentClassKey(instrument);
  const capabilityGate = buildClassKeyedCapabilityGate(accounts, selectedAccounts, manifestByBrokerAndClass, instrumentClass);
  const productAllowed = (opt) => capabilityGate.productAllowed(UI_PRODUCT_TO_CANONICAL[opt] ?? opt);
  const orderTypeAllowed = (t) => capabilityGate.orderTypeAllowed(t);
  const tifAllowed = (v) => capabilityGate.tifAllowed(v);
  //* R6.3/R6.4 — tooltip + "you know this as ___" hint, read directly off the fetched wire for
  //  the selected account's broker (never a hardcoded per-surface string).
  const orderTypeDefinition = (t) => capabilityGate.getDefinition('order_types', t);
  const orderTypeBrokerLabel = (t) => capabilityGate.getBrokerLabel('order_types', t);
  const productDefinition = (opt) => capabilityGate.getDefinition('products', UI_PRODUCT_TO_CANONICAL[opt] ?? opt);
  const productBrokerLabel = (opt) => capabilityGate.getBrokerLabel('products', UI_PRODUCT_TO_CANONICAL[opt] ?? opt);

  //--> Defect B (#28) B5 — the single selected account's 5-state resolution (SmartOrderForm is
  //    single-account, unlike TradeExecutionStep's N-account fan-out), computed off the SAME
  //    `resolveAccountGateState` TradeExecutionStep uses (B2 mirror) so wording never drifts
  //    between the two surfaces. `byClass` is THIS account's broker's classKey map (not the whole
  //    multi-broker `manifestByBrokerAndClass`).
  const selectedAccByClass = selectedAcc
    ? (manifestByBrokerAndClass?.[selectedAcc.broker_id] ?? manifestByBrokerAndClass?.[String(selectedAcc.broker_id)] ?? null)
    : null;
  const selectedAccBrokerStatus = selectedAcc
    ? (brokerLoadStatus?.[selectedAcc.broker_id] ?? brokerLoadStatus?.[String(selectedAcc.broker_id)])
    : undefined;
  const gateState = resolveAccountGateState({
    manifestLoaded,
    instrumentClass,
    brokerStatus: selectedAccBrokerStatus,
    byClass: selectedAccByClass,
  });

  //--> Defect B (#28) B5(b)/RC2 — honest per-option disabled text, mirrors
  //    TradeExecutionStep.jsx's `gateStateTooltip`/`gateStateOptionSuffix` verbatim (same wording,
  //    same precedence) so the two surfaces never drift. Returns `null`/'not supported' for
  //    'OK'/'UNSUPPORTED' so the caller falls through to its OWN existing axis-specific "not
  //    supported" wording (S4 regression guard — the genuine-gap case is unchanged).
  const gateStateTooltip = (state) => {
    if (state === 'UNKNOWN_INSTRUMENT') return 'Complete or reselect the instrument for this strategy';
    if (state === 'LOAD_FAILED') return "Capabilities unavailable — check the broker login/session and retry";
    if (state === 'LOADING') return 'Checking capabilities…';
    return null;
  };
  const gateStateOptionSuffix = (state) => {
    if (state === 'UNKNOWN_INSTRUMENT') return 'complete/reselect instrument';
    if (state === 'LOAD_FAILED') return 'capabilities unavailable';
    if (state === 'LOADING') return 'checking…';
    return 'not supported';
  };

  //--> Defect B (#28) B5(a) — the single-account inline banner (not a full panel — SME B4 —
  //    SmartOrderForm has no dedicated panel real estate like TradeExecutionStep's Step-4). State
  //    C (UNSUPPORTED/genuine gap) renders NO banner here — it still surfaces per-option via the
  //    "— not supported" labels below (unchanged), consistent with this form never having had a
  //    dedicated "can't trade" panel.
  const selectedBrokerDisplayName = selectedAcc
    ? (brokersKeyMap[selectedAcc.broker_id] || brokersKeyMap[String(selectedAcc.broker_id)] || 'This broker')
    : 'This broker';
  const gateStateBanner = selectedAccounts.length > 0
    ? (gateState === 'LOAD_FAILED'
        ? `Capabilities unavailable — check ${selectedBrokerDisplayName}'s login/session and retry`
        : gateState === 'UNKNOWN_INSTRUMENT'
        ? 'Complete or reselect the instrument for this strategy'
        : gateState === 'LOADING'
        ? `Checking ${selectedBrokerDisplayName}'s capabilities…`
        : null)
    : null;

  //--> Auto-switch away from selections the selected broker/instrument-class combination cannot
  //    place (e.g. ICICI: INTRADAY→DELIVERY, MARKET→LIMIT) the moment the account/instrument
  //    changes.
  //! B6b (Defect B #28, RC1 — BLOCKING) — this is the ONLY writer of these three fields, and it
  //! previously fired off the state-blind `productAllowed`/`orderTypeAllowed`/`tifAllowed` alone,
  //! so a broker-login blip (State A), first-paint (LOADING, this form had NO manifestLoaded
  //! guard at all before this fix), or an incomplete instrument (State B) silently rewrote a
  //! genuinely-supported selection and never restored it. Coercion now fires ONLY in state 'OK'.
  useEffect(() => {
    if (gateState !== 'OK') return;
    if (!productAllowed(productType)) setProductType('DELIVERY');
    if (!orderTypeAllowed(orderType)) setOrderType('LIMIT');
    if (!tifAllowed(validity)) setValidity('IOC');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gateState, selectedAccounts, accounts, manifestByBrokerAndClass, brokerLoadStatus, manifestLoaded, instrumentClass]);

  return (
    <div className="h-full flex flex-col gap-3 font-sans">
        
        {/* 1. Buy/Sell Toggle (Legacy Style) */}
        <div className="flex rounded-md overflow-hidden border border-[var(--divider)] shadow-sm">
            <button 
                onClick={() => setAction('BUY')}
                className={`flex-1 py-2 flex flex-col items-center justify-center transition-all ${
                    action === 'BUY' 
                    ? 'bg-profit text-white' 
                    : 'bg-bg-3 dark:bg-bg-2 text-fg-2 hover:bg-bg-3 dark:hover:bg-bg-3'
                }`}
            >
                <span className="text-base font-black uppercase">Buy</span>
                <span className="text-[10px] opacity-80 font-bold uppercase tracking-wider">Long</span>
            </button>
            <div className={`w-0.5 z-10 ${action === 'BUY' ? 'bg-profit' : 'bg-loss'}`}></div>
            <button 
                onClick={() => setAction('SELL')}
                className={`flex-1 py-2 flex flex-col items-center justify-center transition-all ${
                    action === 'SELL' 
                    ? 'bg-loss text-white' 
                    : 'bg-bg-3 dark:bg-bg-2 text-fg-2 hover:bg-bg-3 dark:hover:bg-bg-3'
                }`}
            >
                <span className="text-base font-black uppercase">Sell</span>
                <span className="text-[10px] opacity-80 font-bold uppercase tracking-wider">Short</span>
            </button>
        </div>

        {/* Notification */}
        {/*! D3 (live-found 2026-07-13) — an ERROR MESSAGE MUST NEVER CRASH THE ORDER FORM.
            The operator hit a real 422 (bis_unavailable — a live order was fail-closed) and, instead
            of being told WHY, got a React crash: "Objects are not valid as a React child (found:
            object)". The whole SmartTrade subtree went to the error boundary and the ACTUAL reason
            was never shown. So the one moment the operator most needed a message, the message itself
            took down the screen.

            The catch-block already coerces `msg` to a string (F5). This is the LAST-LINE guarantee at
            the render boundary: whatever reaches here — an object, an array, a FastAPI 422 `detail`
            list, a nested error — is rendered as TEXT, never as a React child. An unreadable error is
            bad; a CRASH that hides the error is far worse, because it destroys the information the
            operator needs to decide whether real money is at risk.

            Belt AND braces is correct here: this render is on the money path's failure path, which is
            exactly where we have the least ability to predict the shape of what arrives. */}
        {notification && (
            <div className={`p-2 rounded text-xs font-bold text-center ${
                notification.type === 'success' ? 'bg-[var(--badge-profit-bg)] text-[var(--badge-profit-fg)]' :
                notification.type === 'warning' ? 'bg-[var(--badge-warn-bg)] text-[var(--badge-warn-fg)]' :
                'bg-[var(--badge-loss-bg)] text-[var(--badge-loss-fg)]'
            }`}>
                {toDisplayText(notification.msg)}
            </div>
        )}
        
        {/* Broker Selection (Legacy Card Style) */}
        <div className="flex flex-col gap-1 px-1">
             <label className="text-[10px] uppercase font-bold text-fg-3">Broker Accounts ({selectedAccounts.length})</label>
             <div className="grid grid-cols-2 gap-2">
                {accounts.length === 0 && <span className="text-xs text-fg-3">Loading accounts...</span>}
                {accounts.map(acc => {
                    const isSelected = selectedAccounts.includes(acc.account_id);
                    
                    // Debugging Log
                    // console.log("Broker Mapping Debug:", { acc, id: acc.broker_id, map: brokersKeyMap });

                    // Resolve Broker Name (Robust Lookup)
                    // Priority 1: Direct from Account (Backend provides this)
                    // Priority 2: Map lookup
                    // Priority 3: Raw ID
                    const rawBrokerId = acc.broker_id;
                    let brokerName = acc.broker_display_name || 'UNKNOWN BROKER';
                    
                    if (!acc.broker_display_name && brokersKeyMap && rawBrokerId) {
                         brokerName = brokersKeyMap[rawBrokerId] || brokersKeyMap[String(rawBrokerId)] || String(rawBrokerId);
                    }
                    brokerName = brokerName.toUpperCase();
                    
                    const userName = acc.account_holder_name || 'Unknown';
                    const userId = acc.broker_user_id || '';
                    const shortName = userName.substring(0, 2).toUpperCase();

                    // Colors based on Action (Buy/Sell) if selected, else Gray
                    const activeBorder = action === 'BUY' ? 'border-profit' : 'border-loss';
                    const activeBg = action === 'BUY' ? 'bg-profit/10' : 'bg-loss/10';
                    const activeIconBg = action === 'BUY' ? 'bg-profit' : 'bg-loss';
                    const activeText = action === 'BUY' ? 'text-profit' : 'text-loss';
                    
                    return (
                        <div
                            key={acc.account_id}
                            onClick={() => toggleAccount(acc.account_id)}
                            className={`relative flex items-center gap-3 p-2 rounded-lg border-2 cursor-pointer transition-all ${
                                isSelected 
                                ? `${activeBorder} ${activeBg}` 
                                : 'border-transparent bg-bg-3 hover:bg-bg-3 dark:hover:bg-bg-3'
                            }`}
                        >
                            {/* Icon Box (Initials of User) */}
                            <div className={`w-10 h-10 rounded-full flex-shrink-0 flex items-center justify-center text-xs font-black shadow-sm ${
                                isSelected 
                                ? `${activeIconBg} text-white` 
                                : 'bg-bg-3 text-fg-2'
                            }`}>
                                {shortName}
                            </div>

                            {/* Details: User Name BIG, Broker Name Small */}
                            <div className="flex flex-col overflow-hidden leading-tight">
                                <span className={`text-sm font-black uppercase truncate ${isSelected ? activeText : 'text-fg-2'}`}>
                                    {userName}
                                </span>
                                <span className="text-[10px] font-bold text-fg-3 truncate mt-0.5">
                                    {brokerName}
                                </span>
                            </div>

                            {/* Checkmark Indicator */}
                            {isSelected && (
                                <div className={`absolute top-1 right-1 w-2 h-2 rounded-full ${activeIconBg}`}></div>
                            )}
                        </div>
                    );

                })}
             </div>
        </div>

        {/* Defect B (#28) B5(a) — honest inline banner (single-account, not a full panel — SME
            B4). Renders ONLY for State A/B/LOADING; State C (genuine gap) renders none here,
            unchanged, and still surfaces per-option via the "— not supported" labels below. */}
        {gateStateBanner && (
            <div className={`px-2 py-1.5 rounded text-[11px] font-semibold text-center ${
                gateState === 'LOADING' ? 'bg-bg-3 text-fg-3' : 'bg-[var(--badge-warn-bg)] text-[var(--badge-warn-fg)]'
            }`}>
                {gateStateBanner}
            </div>
        )}

        {/* 2. Order Type Tabs */}
        <div className="flex border-b border-[var(--divider)]">
            {['MARKET', 'LIMIT', 'SL-M'].map(type => {
                const allowed = orderTypeAllowed(type);
                //* R6.3 — ⓘ tooltip sourced directly off the fetched capability wire's
                //  `definition` field when allowed; R6.1.2 disabled reason otherwise. Never a
                //  hardcoded per-surface string.
                //  Defect B (#28) RC2 — State A/B/LOADING get their own honest tooltip instead of
                //  always claiming "does not permit" (only true in State C/genuine-gap).
                const stateTooltip = gateStateTooltip(gateState);
                const tooltip = allowed
                    ? (orderTypeDefinition(type) || undefined)
                    : (stateTooltip || `${brokersKeyMap[selectedAcc?.broker_id] || 'This broker'} does not permit ${type} orders for this instrument class`);
                return (
                <button
                    key={type}
                    onClick={() => allowed && setOrderType(type)}
                    disabled={!allowed}
                    title={tooltip}
                    className={`flex-1 py-2 text-xs font-bold uppercase tracking-wide border-b-2 transition-colors ${
                        !allowed
                        ? 'border-transparent text-fg-3 cursor-not-allowed line-through'
                        : orderType === type
                        ? `${borderColor} ${themeTextColor}`
                        : 'border-transparent text-fg-3 hover:text-fg-2 dark:hover:text-fg-3'
                    }`}
                >
                    {type}{allowed && orderTypeDefinition(type) ? ' ⓘ' : ''}
                </button>
                );
            })}
        </div>
        {/* R6.4 — "you know this as ___" broker-label hint for the currently selected order type */}
        {orderTypeBrokerLabel(orderType) && (
            <p className="text-[9px] text-fg-3 text-center -mt-1">
                {brokersKeyMap[selectedAcc?.broker_id] || 'This broker'} calls this &quot;{orderTypeBrokerLabel(orderType)}&quot;
            </p>
        )}

        {/* 3. Fields Area */}
        <div className="flex flex-col gap-3 p-1">
            
            {/* Price Row (Hide for Market) */}
            {orderType !== 'MARKET' && (
                <div className="grid grid-cols-2 gap-2">
                    <div className="flex flex-col gap-1">
                        <label className="text-[10px] uppercase font-bold text-fg-3">Price</label>
                        <input 
                            type="number" step="0.05"
                            value={price} onChange={(e) => setPrice(e.target.value)}
                            className="ds-input-trade"
                        />
                    </div>
                    {/* Placeholder for Ticks (Layout matching) or Trigger Price */}
                    <div className="flex flex-col gap-1">
                        <label className="text-[10px] uppercase font-bold text-fg-3">
                            {orderType === 'SL-M' ? 'Trigger Price' : 'Ticks (Offset)'}
                        </label>
                        <input 
                            type="number" step="0.05"
                            value={orderType === 'SL-M' ? triggerPrice : 0} 
                            onChange={(e) => orderType === 'SL-M' && setTriggerPrice(e.target.value)}
                            disabled={orderType !== 'SL-M'}
                            className="ds-input-trade"
                        />
                    </div>
                </div>
            )}

            {/* Qty & Product Row */}
            <div className="grid grid-cols-2 gap-2">
                <div className="flex flex-col gap-1">
                    <label className="text-[10px] uppercase font-bold text-fg-3">Quantity</label>
                    <input 
                        type="number" 
                        value={quantity} onChange={(e) => setQuantity(e.target.value)}
                        className="ds-input-trade"
                    />
                    {instrumentMeta?.lot_size && (
                        <div className="text-[10px] text-fg-3 mt-1">
                            {quantity} lots x {instrumentMeta.lot_size} = {Number(quantity) * instrumentMeta.lot_size} units
                        </div>
                    )}
                </div>
                <div className="flex flex-col gap-1">
                    <label className="text-[10px] uppercase font-bold text-fg-3">Product</label>
                    <select
                        value={productType} onChange={(e) => setProductType(e.target.value)}
                        title={productDefinition(productType) || undefined}
                        className="ds-input-trade"
                    >
                        {/* order-instrument-normalization R6.1.1/R6.2 — options gated by the
                            class-keyed capability manifest (broker + resolved instrument class),
                            REPLACING the old migration-008 api_capabilities gate. */}
                        {/* R6.4 (2026-07-10) — the option LABEL is the CANONICAL term. It used to read
                            'MIS (Intraday)' / 'CNC (Delivery)': broker jargon (and not even FlatTrade's —
                            its intraday code is `I`) shown as the primary label, directly above a
                            per-broker hint that says "<Broker> calls this ___". Two vocabularies in one
                            row. The values were always canonical; only the labels lagged. */}
                        {/* Defect B (#28) RC2 — the disabled suffix is state-branched
                            (`gateStateOptionSuffix`): State C (genuine gap) keeps the
                            "— not supported" string VERBATIM (S4 regression guard); State
                            A/B/LOADING get their own honest suffix — a contradictory "not
                            supported" no longer flashes on every option while the banner above
                            says "checking…"/"reselect instrument"/"capabilities unavailable". */}
                        <option value="INTRADAY" disabled={!productAllowed('INTRADAY')}>
                            {productAllowed('INTRADAY') ? 'Intraday' : `Intraday — ${gateStateOptionSuffix(gateState)}`}
                        </option>
                        <option value="DELIVERY" disabled={!productAllowed('DELIVERY')}>
                            {productAllowed('DELIVERY') ? 'Delivery' : `Delivery — ${gateStateOptionSuffix(gateState)}`}
                        </option>
                        <option value="FUTURES" disabled={!productAllowed('FUTURES')}>
                            {productAllowed('FUTURES') ? 'Futures' : `Futures — ${gateStateOptionSuffix(gateState)}`}
                        </option>
                        <option value="OPTIONS" disabled={!productAllowed('OPTIONS')}>
                            {productAllowed('OPTIONS') ? 'Options' : `Options — ${gateStateOptionSuffix(gateState)}`}
                        </option>
                        {/* F11 (Stage-4 loop-back) — MARGIN is the SOLE MTF representation now;
                            the legacy `<option value="MTF">` (migration-008-gated) is retired. */}
                        <option value="MARGIN" disabled={!productAllowed('MARGIN')}>
                            {productAllowed('MARGIN')
                                ? 'MTF (Margin Trading Facility)'
                                : `MTF (Margin Trading Facility) — ${gateStateOptionSuffix(gateState)}`}
                        </option>
                    </select>
                    {/* R6.4 — "you know this as ___" broker-label hint for the selected product */}
                    {productBrokerLabel(productType) && (
                        <span className="text-[9px] text-fg-3">
                            {brokersKeyMap[selectedAcc?.broker_id] || 'This broker'} calls this &quot;{productBrokerLabel(productType)}&quot;
                        </span>
                    )}
                </div>
            </div>

            {/* Validity Row */}
            <div className="flex flex-col gap-1 mt-1">
                <label className="text-[10px] uppercase font-bold text-fg-3 text-center">Validity</label>
                <div className="flex justify-center bg-bg-3 p-1 rounded-md">
                    {['DAY', 'IOC'].map(val => {
                        //--> R6.2/R3.7 — validity/TIF gated by the class-keyed manifest too (e.g.
                        //    FlatTrade's manifest declares ONLY IOC — it must never advertise DAY
                        //    as independently selectable when the wire silently coerces it, R3.7).
                        //    F11 (Stage-4 loop-back): 'VTC' dropped — formally out-of-taxonomy,
                        //    not a canonical TimeInForce member, not placeable on either live
                        //    adapter; its dead FE affordance is removed, not just disabled.
                        //! Merge (PR #45/#46 design-system): className takes the DS token `bg-bg-3`
                        //!  with the hardcoded `dark:bg-[#2b303b]` retired — theirs. The list, the
                        //!  capability gating and the tooltip are ours. Union, not either/or.
                        const allowed = tifAllowed(val);
                        const tooltip = allowed
                            ? (capabilityGate.getDefinition('tif', val) || undefined)
                            : `${brokersKeyMap[selectedAcc?.broker_id] || 'This broker'} does not permit ${val} orders for this instrument class`;
                        return (
                        <button
                            key={val}
                            onClick={() => allowed && setValidity(val)}
                            disabled={!allowed}
                            title={tooltip}
                            className={`flex-1 py-1 text-[10px] font-bold rounded transition-all ${
                                !allowed
                                ? 'text-fg-3/50 cursor-not-allowed line-through'
                                : validity === val
                                ? 'bg-white dark:bg-bg-3 shadow-sm text-fg-1'
                                : 'text-fg-3 hover:text-fg-2'
                            }`}
                        >
                            {val}
                        </button>
                        );
                    })}
                </div>
            </div>

        </div>

        {/* 4. Submit Button */}
        <div className="mt-auto pt-2">
            {/* Badge: flag-driven — derives from the selected account's `broker_is_paper`.
                M1-R2.2: the mode is READ from the account, never a user-settable toggle.

                Restored on the merge of PR #45/#46 (`67a2323d` dropped it as "the redundant
                mode pill"). It is not decoration: this form places real money, and the
                operator must be able to see PAPER vs LIVE at a glance before submitting.
                Three tests guard it (`SmartOrderForm.test.jsx` M1-R2.2 / F1(b)) and they
                went red when it was removed.

                Styled with the design-system badge component rather than the pre-DS
                hardcoded Tailwind colours. The DS itself declares the semantics:
                `ds-components.css:362` — "Live = profit (green), Paper = info (blue)". */}
            <div className="flex justify-center mb-2">
                <div
                    title={(!isPaperAccount && !liveEnabled) ? 'Live mode disabled by platform config' : undefined}
                    className={`ds-badge sm select-none uppercase tracking-wider ${
                        isPaperAccount ? 'info' : (!liveEnabled ? 'loss' : 'profit')
                    }`}
                >
                    {isPaperAccount ? 'PAPER MODE' : (!liveEnabled ? 'LIVE — BLOCKED' : 'LIVE MODE')}
                </div>
            </div>
            <button
                //! Phone reviews first (confirm Sheet); desktop submits directly as before.
                onClick={isPhone ? () => setConfirmOpen(true) : handleSubmit}
                disabled={isSubmitDisabled}
                className={`w-full py-4 rounded-xl text-white font-black uppercase tracking-wider text-sm shadow-lg active:scale-95 transition-all ${
                    action === 'BUY' 
                    ? 'bg-profit hover:bg-profit/90 shadow-profit/20' 
                    : 'bg-loss hover:bg-loss/90 shadow-loss/20'
                } disabled:opacity-60 disabled:cursor-not-allowed`}
            >
                {loading ? <span className="inline-flex items-center justify-center gap-2"><Loader size="sm" tone="inherit" /> Processing…</span> : `PLACE ${action} ORDER`}
            </button>
             <p className="text-[10px] text-center text-fg-3 mt-2">
                {quantity} Qty @ {orderType === 'MARKET' ? 'MKT' : price} {productType} {validity}
            </p>
        </div>

        {/* Manual Orders Panel */}
        <ManualOrdersPanel userId={user?.id} armedOrders={armedOrders} refreshSignal={ordersRefreshSignal} />

        {/* Phone-only confirm — review the order before it goes to the broker.
            Placement itself still runs through the SAME handleSubmit (capability
            gate, idempotency key, live/paper rules all unchanged). */}
        {isPhone && (
          <Sheet open={confirmOpen} onClose={() => setConfirmOpen(false)} title="Confirm order">
            <div style={{ fontSize: 13 }}>
              {[
                ['Action', `${action} · ${action === 'BUY' ? 'Long' : 'Short'}`, action === 'BUY' ? 'var(--color-profit)' : 'var(--color-loss)'],
                ['Instrument', `${instrument?.symbol || '—'}${instrument?.exchange ? ' · ' + instrument.exchange : ''}`],
                ['Qty · Type', `${quantity} · ${orderType}`],
                ...(orderType !== 'MARKET' ? [['Price', `₹${price}`]] : []),
                ...(orderType === 'SL-M' ? [['Trigger', `₹${triggerPrice}`]] : []),
                ['Product · Validity', `${productType} · ${validity}`],
                ['Account', selectedAcc?.account_holder_name || selectedAcc?.broker_display_name || '—'],
              ].map(([label, value, color], i) => (
                <div
                  key={label}
                  style={{
                    display: 'flex', justifyContent: 'space-between', gap: 12,
                    padding: '8px 0',
                    borderTop: i === 0 ? 'none' : '1px solid var(--border)',
                  }}
                >
                  <span style={{ color: 'var(--fg-3)', flex: '0 0 auto' }}>{label}</span>
                  <span style={{ color: color || 'var(--fg-1)', fontWeight: 600, textAlign: 'right', minWidth: 0 }}>{value}</span>
                </div>
              ))}
            </div>

            {/* The mode you're about to fire — never let this be a surprise. */}
            <div style={{ display: 'flex', justifyContent: 'center', marginTop: 12 }}>
              <span className={`ds-badge sm uppercase tracking-wider ${isPaperAccount ? 'info' : (!liveEnabled ? 'loss' : 'profit')}`}>
                {isPaperAccount ? 'PAPER MODE' : (!liveEnabled ? 'LIVE — BLOCKED' : 'LIVE MODE — REAL MONEY')}
              </span>
            </div>

            <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
              <button
                type="button"
                onClick={() => setConfirmOpen(false)}
                style={{ flex: 1, appearance: 'none', cursor: 'pointer', height: 46, borderRadius: 12, background: 'var(--bg-3)', border: '1px solid var(--border)', color: 'var(--fg-1)', fontSize: 14, fontWeight: 600 }}
              >
                Back
              </button>
              <button
                type="button"
                disabled={isSubmitDisabled}
                onClick={() => { setConfirmOpen(false); handleSubmit(); }}
                style={{
                  flex: 1.4, appearance: 'none', cursor: 'pointer', height: 46, borderRadius: 12, border: 'none',
                  background: action === 'BUY' ? 'var(--color-profit)' : 'var(--color-loss)',
                  color: '#fff', fontSize: 14, fontWeight: 700,
                  opacity: isSubmitDisabled ? 0.6 : 1,
                }}
              >
                Place {action} order
              </button>
            </div>
          </Sheet>
        )}
    </div>
  );
};

export default SmartOrderForm;
